import type { CqgEnvironment } from '@features/cqg/lib/cqgCreds';

/**
 * Per-account login memory — FE-ONLY (localStorage, never the BE/DB; user directive 2026-07-18).
 * Remembers which USERNAME (and CQG environment / Rithmic system) was last used to connect each
 * brokerage account, so the Accounts panel can prefill the connect dialog for that specific account
 * and tag its row DEMO/LIVE even before the server-side environment is known. NEVER stores the
 * password — that stays in the single per-provider credentials blob for the active session only.
 *
 * Keyed by account name (the `AccountInfo.name` filter key). Provider-agnostic: CQG records a
 * `username` + demo/live `environment`; Rithmic records a `username` + `system` (gateway) and has no
 * environment. Only the field a provider actually has is stored.
 */
const STORAGE_KEY = 'accounts.logins.v1';

export interface AccountLogin {
  username: string;
  /** CQG demo/live gateway; null for providers without one (Rithmic). */
  environment: CqgEnvironment | null;
  /** Rithmic gateway/system name (e.g. "Rithmic Test"); absent for CQG. */
  system?: string | null;
}

type LoginMap = Record<string, AccountLogin>;

function readAll(): LoginMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as LoginMap) : {};
  } catch { return {}; }
}

/** The remembered login for one account, or null. */
export function readAccountLogin(name: string): AccountLogin | null {
  const entry = readAll()[name];
  if (!entry || typeof entry.username !== 'string' || !entry.username) return null;
  const environment = entry.environment === 'demo' || entry.environment === 'live' ? entry.environment : null;
  const system = typeof entry.system === 'string' && entry.system ? entry.system : null;
  // Keep the returned shape minimal: only attach `system` for providers that have one (Rithmic), so a
  // CQG entry round-trips as exactly `{ username, environment }`.
  return system ? { username: entry.username, environment, system } : { username: entry.username, environment };
}

/**
 * Record that `names` were last connected with `username` (+ CQG `environment` OR Rithmic `system`).
 * Called after a successful CQG/Rithmic logon once the session's owned accounts are known. No-op
 * without a username.
 */
export function rememberAccountLogins(names: readonly string[], login: AccountLogin): void {
  if (!login.username || names.length === 0) return;
  try {
    const map = readAll();
    for (const name of names) {
      if (!name) continue;
      // Store only the fields the provider actually has — `system` for Rithmic, `environment` for CQG.
      map[name] = login.system
        ? { username: login.username, environment: login.environment, system: login.system }
        : { username: login.username, environment: login.environment };
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch { /* storage unavailable (private mode etc) — non-fatal */ }
}
